import React, { useState } from 'react';
import { HelpCircle, Eye, Info, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { EXPOSITOR_TYPES } from '@/constants/expositorTypes';

interface ExpositorSectionProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
  productParams: any;
  onParamsChange: (params: any) => void;
  selectedOptions: any;
  onOptionsChange: (options: any) => void;
}

export const ExpositorSection: React.FC<ExpositorSectionProps> = ({
  selectedType,
  onTypeChange,
  productParams,
  onParamsChange,
  selectedOptions,
  onOptionsChange
}) => {
  const [showHelp, setShowHelp] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // Kolory i ikony dla każdego typu
  const typeStyles = {
    podstawkowy: { icon: '📐', color: 'from-blue-500 to-blue-600' },
    schodkowy: { icon: '📊', color: 'from-green-500 to-green-600' },
    z_haczykami: { icon: '🪝', color: 'from-purple-500 to-purple-600' },
    wiszacy: { icon: '🖼️', color: 'from-orange-500 to-orange-600' },
    stojak: { icon: '🏢', color: 'from-red-500 to-red-600' },
    kosmetyczny: { icon: '💄', color: 'from-pink-500 to-pink-600' }
  };

  return (
    <div className="space-y-6">
      {/* Nagłówek sekcji */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-4">
        <h2 className="text-xl font-semibold text-white mb-2">
          1.1 Wybierz typ ekspozytora
        </h2>
        <p className="text-orange-100 text-sm">
          Każdy typ ma unikalną konstrukcję. Kliknij ikonę pomocy aby zobaczyć schemat.
        </p>
      </div>

      {/* Grid z typami */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(EXPOSITOR_TYPES).map(([key, type]) => {
          const style = typeStyles[key];
          const isSelected = selectedType === key;
          
          return (
            <div key={key} className="relative">
              {/* Badge popularny */}
              {['podstawkowy', 'schodkowy'].includes(key) && (
                <div className="absolute -top-2 -right-2 z-10 bg-green-500 text-white text-xs px-2 py-1 rounded-full shadow-lg">
                  Popularny
                </div>
              )}
              
              <button
                onClick={() => onTypeChange(key)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? 'border-orange-500 bg-orange-500/10 shadow-xl scale-105'
                    : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`text-3xl p-2 rounded-lg bg-gradient-to-br ${style.color} bg-opacity-20`}>
                      {style.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-white">{type.name}</h3>
                      <p className="text-xs text-gray-400 mt-1">{type.description}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHelp(showHelp === key ? null : key);
                    }}
                    className="p-1 hover:bg-zinc-600 rounded transition-colors"
                  >
                    <HelpCircle size={16} className="text-gray-400" />
                  </button>
                </div>
                
                {/* Wizualizacja ASCII */}
                {showHelp === key && (
                  <div className="mt-3 p-3 bg-zinc-900 rounded-lg text-left">
                    <pre className="text-green-400 font-mono text-xs overflow-x-auto">
                      {type.visualHelp}
                    </pre>
                    <p className="text-xs text-blue-400 mt-2">{type.whenToUse}</p>
                  </div>
                )}
                
                {/* Typowe rozmiary */}
                {isSelected && type.typicalSizes && (
                  <div className="mt-3 pt-3 border-t border-zinc-600">
                    <p className="text-xs text-gray-400 mb-2">Szybki wybór rozmiaru:</p>
                    <div className="flex gap-2">
                      {type.typicalSizes.map((size, idx) => (
                        <button
                          key={idx}
                          className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Tu wywołaj funkcję do ustawienia wymiarów
                            console.log('Ustaw wymiary:', size);
                          }}
                        >
                          {size.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Parametry specyficzne dla typu */}
      {selectedType && EXPOSITOR_TYPES[selectedType] && (
        <div className="bg-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Layers className="text-purple-400" />
            Parametry dla: {EXPOSITOR_TYPES[selectedType].name}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Półki/Poziomy dla schodkowego i kosmetycznego */}
            {['schodkowy', 'kosmetyczny'].includes(selectedType) && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  {selectedType === 'schodkowy' ? 'Liczba poziomów' : 'Liczba półek'}
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onParamsChange({
                      ...productParams,
                      shelves: Math.max(1, (productParams.shelves || 3) - 1)
                    })}
                    className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={productParams.shelves || 3}
                    className="flex-1 px-3 py-2 bg-zinc-700 text-white rounded-lg text-center"
                    readOnly
                  />
                  <button
                    onClick={() => onParamsChange({
                      ...productParams,
                      shelves: Math.min(5, (productParams.shelves || 3) + 1)
                    })}
                    className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
            
            {/* Haczyki dla z_haczykami */}
            {selectedType === 'z_haczykami' && (
              <>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Rzędy haczyków</label>
                  <input
                    type="number"
                    value={productParams.hookRows || 3}
                    onChange={(e) => onParamsChange({
                      ...productParams,
                      hookRows: parseInt(e.target.value) || 3
                    })}
                    className="w-full px-3 py-2 bg-zinc-700 text-white rounded-lg"
                    min="2"
                    max="8"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Kolumny haczyków</label>
                  <input
                    type="number"
                    value={productParams.hookCols || 4}
                    onChange={(e) => onParamsChange({
                      ...productParams,
                      hookCols: parseInt(e.target.value) || 4
                    })}
                    className="w-full px-3 py-2 bg-zinc-700 text-white rounded-lg"
                    min="3"
                    max="10"
                  />
                </div>
              </>
            )}
            
            {/* Kieszenie dla wiszącego */}
            {selectedType === 'wiszacy' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Liczba kieszeni</label>
                <select
                  value={productParams.pockets || 3}
                  onChange={(e) => onParamsChange({
                    ...productParams,
                    pockets: parseInt(e.target.value)
                  })}
                  className="w-full px-3 py-2 bg-zinc-700 text-white rounded-lg"
                >
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'kieszeń' : 'kieszenie'}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Przegródki dla kosmetycznego */}
            {selectedType === 'kosmetyczny' && (
              <div>
                <label className="block text-sm text-gray-400 mb-2">Przegródki na półkę</label>
                <input
                  type="number"
                  value={productParams.partitions || 2}
                  onChange={(e) => onParamsChange({
                    ...productParams,
                    partitions: parseInt(e.target.value) || 0
                  })}
                  className="w-full px-3 py-2 bg-zinc-700 text-white rounded-lg"
                  min="0"
                  max="6"
                />
              </div>
            )}
          </div>
          
          {/* Opcje dodatkowe */}
          <div className="mt-4 space-y-2">
            {/* Topper dla podstawkowego i z haczykami */}
            {['podstawkowy', 'z_haczykami'].includes(selectedType) && (
              <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedOptions.topper || false}
                  onChange={(e) => onOptionsChange({
                    ...selectedOptions,
                    topper: e.target.checked
                  })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Dodaj topper reklamowy (100mm)</span>
              </label>
            )}
            
            {/* Boki dla podstawkowego i schodkowego */}
            {['podstawkowy', 'schodkowy'].includes(selectedType) && (
              <label className="flex items-center gap-3 text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedOptions.boki || false}
                  onChange={(e) => onOptionsChange({
                    ...selectedOptions,
                    boki: e.target.checked
                  })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Dodaj boczne ścianki</span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Porównanie typów */}
      <div>
        <button
          onClick={() => setShowComparison(!showComparison)}
          className="w-full bg-zinc-800 hover:bg-zinc-700 rounded-xl p-4 transition-all flex items-center justify-between"
        >
          <span className="text-white font-semibold flex items-center gap-2">
            <Eye className="text-blue-400" />
            Porównaj wszystkie typy ekspozytorów
          </span>
          {showComparison ? <ChevronUp /> : <ChevronDown />}
        </button>
        
        {showComparison && (
          <div className="mt-4 bg-zinc-800 rounded-xl p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-zinc-700">
                  <th className="text-left py-2">Typ</th>
                  <th className="text-center py-2">Powierzchnia*</th>
                  <th className="text-center py-2">Złożoność</th>
                  <th className="text-left py-2">Najlepsze zastosowanie</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(EXPOSITOR_TYPES).map(([id, type]) => {
                  const style = typeStyles[id];
                  return (
                    <tr key={id} className="text-gray-300 border-b border-zinc-700/50">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{style.icon}</span>
                          <span>{type.name}</span>
                        </div>
                      </td>
                      <td className="text-center">
                        {id === 'podstawkowy' && '~0.20 m²'}
                        {id === 'schodkowy' && '~0.35 m²'}
                        {id === 'z_haczykami' && '~0.45 m²'}
                        {id === 'wiszacy' && '~0.15 m²'}
                        {id === 'stojak' && '~0.50 m²'}
                        {id === 'kosmetyczny' && '~0.80 m²'}
                      </td>
                      <td className="text-center">
                        {id === 'podstawkowy' && <span className="text-green-400">●●○○○</span>}
                        {id === 'schodkowy' && <span className="text-yellow-400">●●●○○</span>}
                        {id === 'z_haczykami' && <span className="text-yellow-400">●●●○○</span>}
                        {id === 'wiszacy' && <span className="text-green-400">●○○○○</span>}
                        {id === 'stojak' && <span className="text-orange-400">●●●●○</span>}
                        {id === 'kosmetyczny' && <span className="text-red-400">●●●●●</span>}
                      </td>
                      <td className="text-xs">{type.whenToUse}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-2">
              * Powierzchnia dla standardowych wymiarów 400×300×200mm
            </p>
          </div>
        )}
      </div>
    </div>
  );
};